import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Mail, Lock, User, ArrowRight, Eye, EyeOff } from 'lucide-react';
import api from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { ThemeToggle } from '@/components/ThemeToggle';
import { BrandLogo } from '@/components/BrandLogo';
import { trackEventOnce } from '@/lib/pixel';

const roleHome = (role) => (role === 'admin' ? '/admin' : '/app');

const errText = (e) => {
  const d = e?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || '').filter(Boolean).join(' ');
  return 'Bir hata oluştu, lütfen tekrar deneyin.';
};

export default function Login({ mode: modeProp }) {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const refCode = (params.get('ref') || '').toUpperCase();
  const utm = {
    utm_source: params.get('utm_source') || undefined,
    utm_medium: params.get('utm_medium') || undefined,
    utm_campaign: params.get('utm_campaign') || undefined,
    ref: refCode || undefined,
  };
  const { user, loading, setUser } = useAuth();
  const [mode, setMode] = useState(modeProp || (refCode ? 'register' : 'login')); // 'login' | 'register'
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', password: '', password2: '', phone: '', city: '', company: '', university: '', department: '', website: '', hearAbout: '' });

  const forgotPassword = async () => {
    const email = form.email.trim();
    if (!email) { toast.error('Önce e-posta adresinizi girin'); return; }
    try {
      await api.post('/auth/forgot-password', { email });
      toast.success('Şifre sıfırlama bağlantısı e-postanıza gönderildi (kayıtlıysa).');
    } catch { toast.error('İşlem başarısız'); }
  };

  useEffect(() => {
    if (!loading && user) navigate(roleHome(user.role));
  }, [user, loading, navigate]);

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/app';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email.trim() || !form.password) { toast.error('E-posta ve şifre gerekli'); return; }
    if (mode === 'register') {
      if (!form.name.trim()) { toast.error('Ad soyad gerekli'); return; }
      if (form.password !== form.password2) { toast.error('Şifreler eşleşmiyor'); return; }
      if (!form.phone.trim()) { toast.error('Telefon gerekli'); return; }
      if (!form.city.trim()) { toast.error('Şehir gerekli'); return; }
      if (!form.university.trim()) { toast.error('Üniversite gerekli'); return; }
      if (!form.department.trim()) { toast.error('Bölüm gerekli'); return; }
      if (!form.hearAbout.trim()) { toast.error('Bizi nereden duyduğunuz gerekli'); return; }
    }
    setBusy(true);
    try {
      const path = mode === 'register' ? '/auth/register' : '/auth/login';
      const body = mode === 'register'
        ? { name: form.name, email: form.email, password: form.password, phone: form.phone, city: form.city, company: form.company, website: form.website, university: form.university, department: form.department, hearAbout: form.hearAbout, ...utm }
        : { email: form.email, password: form.password };
      const res = await api.post(path, body);
      if (mode === 'register') {
        trackEventOnce('CompleteRegistration', {
          utm_source: utm.utm_source || null,
          utm_medium: utm.utm_medium || null,
          utm_campaign: utm.utm_campaign || null,
        }, `reg_${form.email.trim().toLowerCase()}`);
        navigate('/onay-bekliyor');
        return;
      }
      setUser(res.data.user);
      navigate(roleHome(res.data.user.role));
    } catch (e) {
      if (e?.response?.status === 403 && mode === 'login') {
        navigate('/onay-bekliyor');
        return;
      }
      toast.error(errText(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-page flex flex-col md:flex-row" data-testid="login-page">
      <div className="absolute top-4 right-4 z-10">
        <ThemeToggle />
      </div>
      {/* Visual side */}
      <div className="hidden md:block md:w-1/2 relative">
        <img
          src="/login-hero.jpg"
          alt="Bella Maison" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-chrome/45" />
        <div className="absolute bottom-0 left-0 p-12">
          <BrandLogo size="lg" className="text-cream mb-6" />
          <h1 className="font-serif text-4xl text-cream leading-tight max-w-sm">Mimar Partner Programı</h1>
          <p className="text-cream/80 mt-4 max-w-sm text-sm leading-relaxed">
            Müşterinizi mağazaya getirin, QR kodunuzu okutun; indirim ve komisyon otomatik başlasın.
          </p>
        </div>
      </div>

      {/* Form side */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-16">
        <div className="w-full max-w-sm fade-up">
          <div className="text-center mb-8">
            <BrandLogo className="text-ink mb-3 md:hidden mx-auto block" />
            <h2 className="font-serif text-3xl text-ink">{mode === 'register' ? 'Partner Olun' : 'Hoş Geldiniz'}</h2>
            <p className="text-ink/60 text-sm mt-2">
              {mode === 'register' ? 'Kaydol, sürpriz etkinliklere dahil ol' : 'Mimar Partner hesabınıza giriş yapın'}
            </p>
          </div>

          <form onSubmit={submit} className="space-y-3" data-testid="auth-form">
            {mode === 'register' && (
              <InputRow icon={User} testid="auth-name" placeholder="Ad Soyad" required value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
            )}
            <InputRow icon={Mail} testid="auth-email" type="email" placeholder="E-posta" required={mode === 'register'} value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
            <InputRow icon={Lock} testid="auth-password" type="password" placeholder="Şifre" required={mode === 'register'} value={form.password} onChange={(v) => setForm({ ...form, password: v })} />
            {mode === 'register' && (
              <>
                <InputRow icon={Lock} testid="auth-password2" type="password" placeholder="Şifre (Tekrar)" required value={form.password2} onChange={(v) => setForm({ ...form, password2: v })} />
                <div className="grid grid-cols-2 gap-3">
                  <InputRow testid="auth-phone" placeholder="Telefon" required value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} />
                  <InputRow testid="auth-city" placeholder="Şehir" required value={form.city} onChange={(v) => setForm({ ...form, city: v })} />
                </div>
                <InputRow testid="auth-company" placeholder="Firma / Ofis adı (ops.)" value={form.company} onChange={(v) => setForm({ ...form, company: v })} />
                <div className="grid grid-cols-2 gap-3">
                  <InputRow testid="auth-university" placeholder="Üniversite" required value={form.university} onChange={(v) => setForm({ ...form, university: v })} />
                  <InputRow testid="auth-department" placeholder="Bölüm" required value={form.department} onChange={(v) => setForm({ ...form, department: v })} />
                </div>
                <InputRow testid="auth-website" placeholder="Web sitesi / Instagram (ops.)" value={form.website} onChange={(v) => setForm({ ...form, website: v })} />
                <InputRow testid="auth-hearabout" placeholder="Bizi nereden duydunuz?" required value={form.hearAbout} onChange={(v) => setForm({ ...form, hearAbout: v })} />
                <p className="text-xs text-ink/50" data-testid="required-note"><span className="text-rose-500">*</span> işaretli alanlar zorunludur</p>
              </>
            )}
            <button
              type="submit"
              data-testid="auth-submit"
              disabled={busy}
              className="w-full flex items-center justify-center gap-2 bg-ink text-ground py-3.5 rounded-xl font-semibold transition-transform active:scale-95 disabled:opacity-50"
            >
              {busy ? 'Lütfen bekleyin...' : (mode === 'register' ? 'Kayıt Ol' : 'Giriş Yap')}
              {!busy && <ArrowRight strokeWidth={1.5} className="w-4 h-4" />}
            </button>
          </form>

          <button
            data-testid="auth-toggle-mode"
            onClick={() => navigate(`${mode === 'register' ? '/giris' : '/kayit'}${window.location.search || ''}`)}
            className="w-full mt-4 text-sm text-ink/60 hover:text-ink"
          >
            {mode === 'register'
              ? 'Zaten hesabınız var mı? Giriş yapın'
              : 'Hesabınız yok mu? Mimar olarak kayıt olun'}
          </button>

          {mode === 'login' && (
            <button data-testid="forgot-password-link" onClick={forgotPassword}
              className="w-full mt-2 text-xs text-ink-faint hover:text-ink">
              Şifremi unuttum
            </button>
          )}

          <div className="flex items-center gap-3 my-6">
            <div className="flex-1 h-px bg-line" />
            <span className="text-xs text-ink-faint uppercase tracking-wider">veya</span>
            <div className="flex-1 h-px bg-line" />
          </div>

          <button
            data-testid="google-login-btn"
            onClick={googleLogin}
            className="w-full flex items-center justify-center gap-3 border border-line bg-transparent text-ink py-3.5 rounded-xl font-medium transition-transform active:scale-95"
          >
            <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="w-5 h-5 bg-white rounded-full p-0.5" />
            Google ile Giriş Yap
          </button>

          <button
            data-testid="admin-login-link"
            onClick={() => navigate('/yonetici')}
            className="w-full mt-6 text-xs text-ink-faint hover:text-ink tracking-wide"
          >
            Yönetici Girişi
          </button>
        </div>
      </div>
    </div>
  );
}

const InputRow = ({ icon: Icon, testid, type = 'text', placeholder, value, onChange, required = false }) => {
  const [show, setShow] = useState(false);
  const isPw = type === 'password';
  const inputType = isPw && show ? 'text' : type;
  const rightPad = isPw && required ? 'pr-14' : (isPw || required ? 'pr-9' : 'pr-3.5');
  return (
    <div className="relative">
      {Icon && <Icon strokeWidth={1.5} className="w-4 h-4 text-ink-faint absolute left-3.5 top-1/2 -translate-y-1/2" />}
      <input
        data-testid={testid}
        type={inputType}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`w-full bg-ground border border-line rounded-xl py-3 text-sm text-ink placeholder:text-ink-faint focus:outline-none focus:ring-2 focus:ring-gold/40 focus:border-gold transition-colors ${Icon ? 'pl-10' : 'pl-3.5'} ${rightPad}`}
      />
      <div className="absolute right-3.5 top-1/2 -translate-y-1/2 flex items-center gap-2">
        {required && <span aria-hidden="true" className="text-rose-500 pointer-events-none">*</span>}
        {isPw && (
          <button type="button" data-testid={`${testid}-toggle`} onClick={() => setShow((s) => !s)}
            className="text-ink-faint hover:text-ink transition-colors" aria-label={show ? 'Şifreyi gizle' : 'Şifreyi göster'}>
            {show ? <EyeOff strokeWidth={1.5} className="w-4 h-4" /> : <Eye strokeWidth={1.5} className="w-4 h-4" />}
          </button>
        )}
      </div>
    </div>
  );
};
